/**
 * Extração defensiva dos campos de um valor observado como falha.
 *
 * Lógica pura, primeira etapa do pipeline: tudo o que vem depois trabalha sobre `name` e
 * `message` já garantidos como string. A referência ao valor original segue no resultado
 * porque é ela — e não a cópia dos campos — que carrega o stack nativo até a emissão.
 */
import { NormalizedError } from './types';

/** Nome usado quando o valor observado não expõe um `name` aproveitável. */
const DEFAULT_ERROR_NAME = 'Error';

/**
 * Erro sintético para valor que não é `Error`, preservando na mensagem o que foi
 * entregue. Nunca lança.
 *
 * A conversão usa `String(value)` e não interpolação: `String(Symbol())` funciona, o
 * template literal lança. O `try/catch` cobre o que nem `String` converte — objeto sem
 * protótipo não tem `toString` para o qual recorrer.
 */
export function toError(value: unknown): Error {
  let message: string;

  try {
    message = String(value);
  } catch {
    message = '';
  }

  return new Error(message);
}

/**
 * Extrai `name`, `message` e `componentStack` sem nunca lançar.
 *
 * A leitura dos dois campos fica num único `try/catch` de propósito: um `Proxy` ou um
 * getter hostil derruba a extração inteira, e o resultado cai nos defaults em bloco. A
 * alternativa — um `try` por campo — salvaria um campo de um valor que já se provou
 * hostil, ao custo de um caminho a mais para auditar no lugar onde o pipeline menos pode
 * falhar.
 */
export function normalizeError(value: unknown, componentStack?: string | null): NormalizedError {
  let name = DEFAULT_ERROR_NAME;
  let message = '';

  try {
    const source = value as { name?: unknown; message?: unknown } | null | undefined;
    const rawName = source?.name;
    const rawMessage = source?.message;

    if (typeof rawName === 'string' && rawName.length > 0) {
      name = rawName;
    }

    if (typeof rawMessage === 'string') {
      message = rawMessage;
    } else if (rawMessage !== undefined && rawMessage !== null) {
      message = String(rawMessage);
    }
  } catch {
    // Campo hostil: ficam os defaults, e o pipeline segue com o valor original intacto.
  }

  // `componentStack` vazio é equivalente a ausente: a propriedade só é emitida quando
  // tem conteúdo, e uma string vazia no payload seria ruído sem leitura possível.
  return componentStack
    ? { original: value, name, message, componentStack }
    : { original: value, name, message };
}
